"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Datepicker = exports.WrappedDatePicker = void 0;
var tslib_1 = require("tslib");
// (C) 2020-2022 GoodData Corporation
var react_1 = tslib_1.__importDefault(require("react"));
var uuid_1 = require("uuid");
var debounce_1 = tslib_1.__importDefault(require("lodash/debounce"));
var noop_1 = tslib_1.__importDefault(require("lodash/noop"));
var format_1 = tslib_1.__importDefault(require("date-fns/format"));
var parse_1 = tslib_1.__importDefault(require("date-fns/parse"));
var isValid_1 = tslib_1.__importDefault(require("date-fns/isValid"));
var isSameDay_1 = tslib_1.__importDefault(require("date-fns/isSameDay"));
var locale_1 = require("date-fns/locale");
var classnames_1 = tslib_1.__importDefault(require("classnames"));
var sdk_ui_1 = require("@gooddata/sdk-ui");
var react_intl_1 = require("react-intl");
var react_day_picker_1 = require("react-day-picker");
var overlay_1 = require("../utils/overlay");
var domUtilities_1 = require("../utils/domUtilities");
var platform_1 = require("../constants/platform");
var DATEPICKER_OUTSIDE_DAY_SELECTOR = "rdp-day_outside";
var convertedLocales = {
    "en-US": locale_1.enUS,
    "de-DE": locale_1.de,
    "es-ES": locale_1.es,
    "fr-FR": locale_1.fr,
    "ja-JP": locale_1.ja,
    "nl-NL": locale_1.nl,
    "pt-BR": locale_1.ptBR,
    "pt-PT": locale_1.pt,
    "zh-Hans": locale_1.zhCN,
    "ru-RU": locale_1.ru,
};
function formatDate(date, dateFormat) {
    return format_1.default(date, dateFormat);
}
function convertLocale(locale) {
    return convertedLocales[locale];
}
function parseDate(str, dateFormat) {
    try {
        var parsedDate = parse_1.default(str, dateFormat, new Date());
        // parse only dates with 4-digit years. this mimics moment.js behavior - it parses only dates above 1900
        // this is to make sure that the picker input is not overwritten in the middle of writing the year with year "0002" when writing 2020.
        //
        // it's also necessary to parse only when the input string fully matches with the desired format
        // to make sure that the picker input is not overwritten in the middle of writing.
        // e.g, let's consider a case where dateFormat is "dd/MM/yyyy" and the DayPickerInput has already been filled with a valid string "13/09/2020",
        // then an user wants to change only the month "13/09/2020" -> "13/11/2020" by removing "09" and typing "11".
        // in such case the parsing should wait until the user completes typing "11" (otherwise if parsing is done right after the first "1" is typed,
        // the cursor automatically moves to the end of the string in the middle of writing, causing a bad experience for the user).
        if (isValid_1.default(parsedDate) &&
            parsedDate.getFullYear() >= 1000 &&
            str === formatDate(parsedDate, dateFormat)) {
            return parsedDate;
        }
        return;
    }
    catch (_a) {
        return;
    }
}
var WrappedDatePicker = /** @class */ (function (_super) {
    tslib_1.__extends(WrappedDatePicker, _super);
    function WrappedDatePicker(props) {
        var _this = _super.call(this, props) || this;
        _this.datePickerContainerRef = react_1.default.createRef();
        _this.inputRef = react_1.default.createRef();
        _this.resizeHandler = debounce_1.default(function () { return _this.alignDatePicker(); }, 100);
        var alignPoints = props.alignPoints, date = props.date, dateFormat = props.dateFormat;
        _this.state = {
            align: alignPoints[0].align,
            selectedDate: date,
            monthDate: date,
            inputValue: formatDate(date || new Date(), dateFormat),
            isOpen: false,
        };
        _this.handleDayChanged = _this.handleDayChanged.bind(_this);
        _this.handleMonthChanged = _this.handleMonthChanged.bind(_this);
        _this.handleInputChanged = _this.handleInputChanged.bind(_this);
        _this.alignDatePicker = _this.alignDatePicker.bind(_this);
        _this.setComponentRef = _this.setComponentRef.bind(_this);
        _this.handleWrapperClick = _this.handleWrapperClick.bind(_this);
        _this.handleClickOutside = _this.handleClickOutside.bind(_this);
        _this.onKeyDown = _this.onKeyDown.bind(_this);
        return _this;
    }
    WrappedDatePicker.prototype.componentDidMount = function () {
        var _a = this.props, date = _a.date, dateFormat = _a.dateFormat;
        this.setState({ selectedDate: this.updateDate(date || new Date()) });
        this.setState({ inputValue: formatDate(date || new Date(), dateFormat) });
        window.addEventListener("resize", this.resizeHandler);
        document.addEventListener("mousedown", this.handleClickOutside);
    };
    WrappedDatePicker.prototype.UNSAFE_componentWillReceiveProps = function (nextProps) {
        var props = this.props;
        if (props.date > nextProps.date || props.date < nextProps.date) {
            var selectedDate = this.updateDate(nextProps.date);
            this.setState({ selectedDate: selectedDate });
            this.setState({ monthDate: selectedDate });
            this.setState({ inputValue: formatDate(selectedDate, props.dateFormat) });
        }
    };
    WrappedDatePicker.prototype.componentWillUnmount = function () {
        window.removeEventListener("resize", this.resizeHandler);
        document.removeEventListener("mousedown", this.handleClickOutside);
    };
    WrappedDatePicker.prototype.handleClickOutside = function (event) {
        if (this.datePickerContainerRef.current &&
            !this.datePickerContainerRef.current.contains(event.target) &&
            this.inputRef &&
            !this.inputRef.current.contains(event.target)) {
            this.setState({ isOpen: false });
        }
    };
    WrappedDatePicker.prototype.componentDidUpdate = function (_prevProps, prevState) {
        if (this.state.isOpen && !prevState.isOpen) {
            this.alignDatePicker();
        }
    };
    WrappedDatePicker.prototype.setComponentRef = function (ref) {
        this.rootRef = ref;
    };
    WrappedDatePicker.prototype.getInputClasses = function () {
        return classnames_1.default("input-text", "small-12", this.props.size, "gd-datepicker-input-" + uuid_1.v4());
    };
    WrappedDatePicker.prototype.getComponentClasses = function () {
        return classnames_1.default("gd-datepicker", this.props.className, this.props.size, "gd-datepicker-input", this.state.isOpen ? "gd-datepicker-focused" : "");
    };
    WrappedDatePicker.prototype.getOverlayWrapperClasses = function () {
        var _a = tslib_1.__read(this.state.align.split(" "), 2), inputAnchorPoint = _a[0], pickerAnchorPoint = _a[1];
        return classnames_1.default("gd-datepicker-picker", "gd-datepicker-OverlayWrapper", "gd-datepicker-OverlayWrapper-" + inputAnchorPoint + "-xx", "gd-datepicker-OverlayWrapper-xx-" + pickerAnchorPoint);
    };
    WrappedDatePicker.prototype.updateDate = function (date) {
        return this.normalizeDate(date);
    };
    WrappedDatePicker.prototype.handleInputChanged = function (e) {
        var _this = this;
        var value = e.target.value;
        var parsedDate = parseDate(value, this.props.dateFormat);
        this.setState({ inputValue: value });
        if (parsedDate) {
            this.setState({
                selectedDate: parsedDate,
                monthDate: parsedDate,
            }, function () {
                _this.props.onChange(_this.state.selectedDate);
            });
        }
        else {
            if (this.props.resetOnInvalidValue) {
                this.setState({
                    selectedDate: this.state.selectedDate,
                    monthDate: this.state.selectedDate,
                });
                return;
            }
            this.setState({
                selectedDate: undefined,
                monthDate: undefined,
            }, function () {
                _this.props.onChange(null);
            });
        }
    };
    WrappedDatePicker.prototype.handleDayChanged = function (newlySelectedDate) {
        var _this = this;
        if (!newlySelectedDate) {
            this.setState({ isOpen: false });
            return;
        }
        if (isSameDay_1.default(this.state.selectedDate, newlySelectedDate)) {
            this.setState({ isOpen: false });
            return;
        }
        this.inputRef.current.focus();
        this.setState({
            selectedDate: newlySelectedDate,
            monthDate: newlySelectedDate,
            inputValue: formatDate(newlySelectedDate, this.props.dateFormat),
            isOpen: false,
        }, function () {
            _this.props.onChange(newlySelectedDate);
        });
    };
    WrappedDatePicker.prototype.handleMonthChanged = function (month) {
        this.inputRef.current.focus();
        this.setState({ monthDate: month });
    };
    WrappedDatePicker.prototype.normalizeDate = function (date) {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    };
    WrappedDatePicker.prototype.alignDatePicker = function () {
        var _this = this;
        var alignPoints = this.props.alignPoints;
        var container = this.datePickerContainerRef.current.parentElement;
        if (!alignPoints || !container)
            return;
        var optimalAlignment = overlay_1.getOptimalAlignment({
            targetRegion: domUtilities_1.elementRegion(this.rootRef),
            selfRegion: domUtilities_1.elementRegion(container),
            alignPoints: alignPoints,
        });
        var align = optimalAlignment.alignment.align;
        this.setState({
            align: align,
        }, function () {
            _this.props.onAlign(align);
        });
    };
    WrappedDatePicker.prototype.onKeyDown = function (e) {
        if (e.key === "Escape" || e.key === "Tab") {
            this.setState({ isOpen: false });
        }
    };
    WrappedDatePicker.prototype.handleWrapperClick = function (e) {
        var classList = e.target.classList;
        /**
         * Prevent default fixes bug BB-332 but prevents in closing other dropdowns (Bug BB-1102)
         * so we want to prevent default only when clicking on outside dates in datepicker
         */
        if (e.target && classList && classList.contains(DATEPICKER_OUTSIDE_DAY_SELECTOR)) {
            e.preventDefault();
        }
    };
    WrappedDatePicker.prototype.render = function () {
        var _this = this;
        var _a = this.state, inputValue = _a.inputValue, selectedDate = _a.selectedDate, monthDate = _a.monthDate, isOpen = _a.isOpen;
        var _b = this.props, placeholder = _b.placeholder, intl = _b.intl, tabIndex = _b.tabIndex;
        var classNamesProps = {
            root: this.getOverlayWrapperClasses(),
        };
        return (react_1.default.createElement("div", { role: "datepicker", className: this.getComponentClasses(), ref: this.setComponentRef, onClick: this.handleWrapperClick },
            react_1.default.createElement("input", { onKeyDown: this.onKeyDown, tabIndex: tabIndex, onClick: function () { return _this.setState({ isOpen: true }); }, onFocus: function () { return _this.setState({ isOpen: true }); }, ref: this.inputRef, value: inputValue, className: this.getInputClasses(), placeholder: placeholder, onChange: this.handleInputChanged }),
            isOpen ? (react_1.default.createElement("div", { role: "datepicker-picker", ref: this.datePickerContainerRef },
                react_1.default.createElement(react_day_picker_1.DayPicker, { classNames: classNamesProps, locale: convertLocale(intl.locale), showOutsideDays: true, mode: "single", onSelect: this.handleDayChanged, selected: selectedDate, month: monthDate, onMonthChange: this.handleMonthChanged }))) : null,
            react_1.default.createElement("span", { className: "gd-datepicker-icon gd-icon-calendar" })));
    };
    WrappedDatePicker.defaultProps = {
        className: "",
        date: new Date(),
        placeholder: "",
        onChange: noop_1.default,
        resetOnInvalidValue: false,
        size: "",
        tabIndex: 0,
        alignPoints: [{ align: "bl tl" }, { align: "br tr" }, { align: "tl bl" }, { align: "tr br" }],
        onAlign: noop_1.default,
        dateFormat: platform_1.DEFAULT_DATE_FORMAT,
    };
    return WrappedDatePicker;
}(react_1.default.PureComponent));
exports.WrappedDatePicker = WrappedDatePicker;
var DatePickerWithIntl = react_intl_1.injectIntl(WrappedDatePicker);
/**
 * @internal
 */
var Datepicker = /** @class */ (function (_super) {
    tslib_1.__extends(Datepicker, _super);
    function Datepicker() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    Datepicker.prototype.render = function () {
        return (react_1.default.createElement(sdk_ui_1.IntlWrapper, { locale: this.props.locale },
            react_1.default.createElement(DatePickerWithIntl, tslib_1.__assign({}, this.props))));
    };
    return Datepicker;
}(react_1.default.PureComponent));
exports.Datepicker = Datepicker;
//# sourceMappingURL=Datepicker.js.map